import { cloneDeep, concat, flatten, isEmpty, isEqual, set } from 'lodash';
import moment from 'moment';
import config from 'config';
import {
  USAGE_EVENTS,
  clearLocations,
  clearMapData,
  clearOrders,
  clearSolution,
  clearVehicleProfiles,
  recordUsageEvent,
  setError,
  setDisplayParam,
  setMapData,
  setOrders,
  setProblem,
  setProblemParameter,
  setSolution,
  setSolutionJson,
  setSolutionParam,
  setTerritoriesFromProblemFile,
  setTerritoryParameter,
  setTourParameter,
  setUserParam,
  submitProblem,
} from 'actions';
import axios from 'axios';
import { ORDER_ACTIVITIES, ORDER_PRIORITY } from './csv/config';
import { convertProblemToGeoJSON, convertToGeoJSON } from './GeoJSONConverter';
import {
  generateAvoidAreasFromFileProblem,
  generateExcludeAreasFromFileProblem,
  generatePolygonsFromOrders,
} from './territories/TerritoriesFromJson';
import { APP_MODES, getFileHost, getFileUrlPath } from './urlHelpers';
import {
  getStopLocation,
  createStopsNoLocation,
  isDepartureOrArrival,
  isBreakOrReload,
} from './SolutionHelpers';
import { isProdEnv } from './helpers';
import { convertCostsToTourPlanner } from './cost';

const {
  defaults: {
    limits: { maxDistance, shiftTime },
  },
} = config;

const addActivityToJobs = (tasks, activityType) => {
  if (!tasks || isEmpty(tasks)) return [];
  return tasks.map((task) => {
    const newTask = cloneDeep(task);
    newTask.activity = activityType;
    return newTask;
  });
};

export const createOrder = (
  location,
  id,
  tag,
  activity,
  priority,
  demand = undefined,
  skills = undefined,
  category = undefined,
  position = undefined,
  groupId = undefined,
  territoryIds = undefined,
  orders,
  index,
  duration,
  times,
  placement,
  maxTimeOnVehicle,
) => {
  const findOrder = orders && orders.find((ord) => ord.InternalID === id);
  return {
    Latitude: location.lat,
    Longitude: location.lng,
    Name: `${id} - ${activity}`,
    Address: findOrder?.Address || tag || id,
    Demand: demand,
    ID: `${id}${index ? `-${index}` : ''}`,
    InternalID: id,
    Priority: priority,
    Activity: activity,
    Group: groupId,
    Territories: territoryIds,
    Position: position,
    NameHint: location.nameHint,
    Skills: skills,
    Category: category,
    TimeCreated: Date.now(),
    SideOfStreet: location.sideOfStreetHint,
    Duration: duration,
    TimeWindow: times,
    Placement: placement,
    MaxTime: maxTimeOnVehicle,
  };
};

export const generateOrdersFromFileProblem = (file, orders) => {
  if (!file || !file.plan) return [];

  const pudoJobs =
    file.plan.groups
      ?.filter((group) => Array.isArray(group.pudos) && group.pudos.length > 0)
      .flatMap(({ id: groupId, pudos, placement }) =>
        pudos.flatMap(({ id, places, assignAt }) =>
          places.map(({ location, tag }) =>
            createOrder(
              location,
              id,
              tag,
              ORDER_ACTIVITIES.PUDO,
              ORDER_PRIORITY.NORMAL,
              undefined,
              undefined,
              undefined,
              assignAt,
              groupId,
              placement,
            ),
          ),
        ),
      ) || [];

  const jobs = file.plan.jobs.flatMap(
    ({ id, tasks, priority, skills, category, maxTimeOnVehicle }) => {
      const deliveries = addActivityToJobs(tasks?.deliveries, ORDER_ACTIVITIES.DELIVERY);
      const pickups = addActivityToJobs(tasks?.pickups, ORDER_ACTIVITIES.PICKUP);
      const addIndex = pickups.length > 1 || deliveries.length > 1;

      return [...deliveries, ...pickups]
        .filter((task) => !isEmpty(task))
        .flatMap(({ places, activity, demand, order, position }, index) =>
          places
            .filter(({ location }) => !isEmpty(location))
            .map(({ location, tag, groupId, duration, territoryIds, times }) =>
              createOrder(
                location,
                id,
                tag,
                activity,
                priority,
                demand,
                skills,
                category,
                order || position?.value || position?.type,
                groupId,
                territoryIds,
                orders,
                addIndex && index,
                duration,
                times,
                null,
                maxTimeOnVehicle,
              ),
            ),
        );
    },
  );

  return [...pudoJobs, ...jobs];
};

const activitiesToFilter = [
  'departure',
  'arrival',
  'break',
  'drivingRestTime',
  'workingRestTime',
  'dutyWorkingRestTime',
  'dutyDrivingRestTime',
];
export const generateOrdersFromFileSolution = (file, stopsNoLocation) => {
  if (!file || !file.tours || isEmpty(file.tours)) return [];

  const validOrders = concat(
    flatten(
      file.tours.map(({ stops }, tourIndex) =>
        flatten(
          concat(stops).map((stop, stopIndex) =>
            stop.activities.map(({ location: actLocation, jobId, jobTag, type }) => {
              const location =
                actLocation || getStopLocation(stop, stopsNoLocation, stopIndex, tourIndex);
              return createOrder(location, jobId, jobTag, type);
            }),
          ),
        ),
      ),
    ),
    [],
  ).filter((order) => !activitiesToFilter.includes(order.InternalID));

  const unassignedOrders = file.unassigned
    ? file.unassigned.map((job) => ({
        Latitude: 0,
        Longitude: 0,
        Name: job.jobId,
        Address: job.jobId,
        Demand: [0],
        ID: job.jobId,
        InternalID: job.jobId,
        Priority: ORDER_PRIORITY.NORMAL,
      }))
    : [];

  return concat(validOrders, unassignedOrders);
};

export const generateOrdersFromFileJSON = (file, stopsNoLocation = undefined, orders) => {
  if (!file) return [];
  return file.tours && stopsNoLocation
    ? generateOrdersFromFileSolution(file, stopsNoLocation)
    : generateOrdersFromFileProblem(file, orders);
};

export const generateJobsFromSolution = (file) => {
  if (!file || !file.tours || isEmpty(file.tours)) return [];

  const allActivities = file.tours.flatMap((tour) =>
    tour.stops.flatMap((stop) =>
      stop.activities.map((activity) => ({ ...activity, stopLocation: stop.location })),
    ),
  );

  const filteredActivities = allActivities.filter(
    (activity) => !isDepartureOrArrival(activity.jobId) && !isBreakOrReload(activity.jobId),
  );

  const dividedActivities = filteredActivities.reduce(
    ((hash) => (acc, curr) => {
      if (!hash[curr.jobId]) {
        hash[curr.jobId] = [];
        acc.push(hash[curr.jobId]);
      }
      hash[curr.jobId].push(curr);
      return acc;
    })(Object.create(null)),
    [],
  );

  return dividedActivities.map((job) => {
    const jobObject = {
      id: job[0].jobId,
      tasks: {},
    };

    const deliveries = job
      .filter((activity) => activity.type === 'delivery')
      .map((delivery) => {
        const location = delivery.location || delivery.stopLocation;
        return {
          places: [
            {
              duration: 1,
              location,
            },
          ],
          demand: [0],
        };
      });

    const pickups = job
      .filter((activity) => activity.type === 'pickup')
      .map((pickup) => {
        const location = pickup.location || pickup.stopLocation;
        return {
          places: [
            {
              duration: 1,
              location,
            },
          ],
          demand: [0],
        };
      });

    if (deliveries) jobObject.tasks.deliveries = deliveries;
    if (pickups) jobObject.tasks.pickups = pickups;

    return jobObject;
  });
};

export const generateRequestFromSolution = (file) => {
  if (!file) return null;

  return {
    fleet: {},
    plan: {
      jobs: generateJobsFromSolution(file),
    },
  };
};

export const clearAll = (
  dispatch,
  shouldClearOrders,
  shouldClearMap,
  index,
  shouldClearLocations,
  limits,
  csvFile,
) => {
  dispatch(clearSolution({ limits, csvFile }));
  dispatch(clearVehicleProfiles(index));
  if (shouldClearLocations) dispatch(clearLocations());
  if (shouldClearOrders) dispatch(clearOrders({ index }));
  if (shouldClearMap) dispatch(clearMapData());
};

export const planTour = ({
  dispatch,
  isSolution,
  cookies,
  user,
  oAuth,
  fileObj,
  stopsNoLocation,
  index,
  keepOrders,
  timesPlanned,
  isTDVRP,
  selectedTour,
}) => {
  dispatch(setTerritoryParameter({ planned: true, index }));
  dispatch(setSolutionParam({ param: { timesPlanned, isTDVRP }, index }));

  if (!isSolution) {
    const apiKey = cookies.apikey;
    dispatch(
      submitProblem({ json: fileObj, user, oAuth, apiKey, index, keepOrders, selectedTour }),
    );
  } else {
    dispatch(setSolution({ data: fileObj, stopsNoLocation, keepOrders }));
  }
};

export const uploadProblemAndSolution = (
  problem,
  solution,
  mode,
  problemFilename,
  solutionFilename,
  user,
  cookies,
  oAuth,
  dispatch,
  error,
  index,
  keepOrders,
) => {
  dispatch(setSolutionParam({ param: { name: solutionFilename } }));
  dispatch(setSolutionParam({ param: { problemFilename } }));
  dispatch(setSolutionJson({ json: true }));

  const orderList = generateOrdersFromFileJSON(problem);
  if (isEmpty(orderList)) {
    dispatch(setError(error.invalidFileFormat));
    dispatch(setSolutionJson({ jsonUserChange: false, jsonPaste: false, jsonPending: false }));
    return;
  }

  const stopsNoLocation = createStopsNoLocation(solution.tours);

  const fleet = problem.fleet;
  const vehicleProfiles = fleet.profiles.map((profile) => {
    return {
      name: profile.name,
      avoid: profile.avoid,
      exclude: profile.exclude,
      fleetType: profile.type,
      departureTime: profile.departureTime,
      options: profile.options,
    };
  });
  const vehicles = fleet.types.map(({ shifts, ...item }) => {
    return {
      ...item,
      limits: {
        maxDistance: {
          enabled: item.limits?.maxDistance !== undefined,
          value: item.limits?.maxDistance ?? maxDistance,
        },
        shiftTime: {
          enabled: item.limits?.shiftTime !== undefined,
          value: item.limits?.shiftTime ?? shiftTime,
        },
      },
    };
  });
  const startDate = fleet.types[0].shifts[0].start.time;

  dispatch(
    setTourParameter({
      vehicles,
      vehicleProfiles,
      traffic: fleet.traffic,
      offset: moment.parseZone(startDate).utcOffset(),
    }),
  );
  dispatch(setTourParameter({ fileType: 'json' }));
  dispatch(setOrders(orderList));

  const { territories = [], groupAreas = [] } = generatePolygonsFromOrders(orderList);
  const avoidAreas = generateAvoidAreasFromFileProblem(problem, orderList) || [];
  const excludeAreas = generateExcludeAreasFromFileProblem(problem, orderList) || [];
  dispatch(setTerritoriesFromProblemFile({ territories, avoidAreas, groupAreas, excludeAreas }));
  const polygons = [...territories, ...avoidAreas, ...groupAreas, ...excludeAreas];

  const geo = convertToGeoJSON(
    solution,
    stopsNoLocation,
    problem.plan.jobs,
    orderList,
    null,
    polygons,
  ).geo;
  if (geo) dispatch(setMapData({ geo }));
  else dispatch(clearMapData());

  const toSet = {
    territories: polygons,
    orders: orderList,
    json: problem,
    problem,
    uploaded: true,
    geo,
    filename: solutionFilename,
  };
  dispatch(setProblem(toSet));

  dispatch(recordUsageEvent({ event: USAGE_EVENTS.ORDERS_ADD, mode }));

  planTour({
    dispatch,
    isSolution: true,
    cookies,
    user,
    oAuth,
    fileObj: solution,
    stopsNoLocation,
    index,
    keepOrders,
  });

  dispatch(setSolutionJson({ jsonUserChange: false, jsonPaste: false, jsonPending: false }));
};

export const integrateFileText = (
  txt,
  mode,
  filename,
  user,
  cookies,
  oAuth,
  dispatch,
  isJsonViewer,
  error,
  index,
  keepOrders,
  useIndex,
  isDemo,
  startTrans,
  endTrans,
  jsonNoTerritories,
) => {
  let fileObj;
  try {
    fileObj = JSON.parse(txt);
  } catch {
    dispatch(setError(error.invalidFileFormat));
    return;
  }
  const isTDVRP = fileObj?.configuration?.experimentalFeatures?.[0] === 'time-dependent-vrp';
  const isSolution = !!fileObj.tours;
  const idx = useIndex ? index : undefined;
  const unassignedSol = fileObj?.tours?.length === 0 && (fileObj.notices || fileObj.unassigned);

  dispatch(setSolutionJson({ jsonNoPaste: true }));

  if (!isSolution && !isTDVRP) {
    dispatch(setUserParam({ trafficMatrix: null }));
  }
  if (!isSolution) dispatch(setSolutionJson({ json: true }));

  if (!isSolution && isProdEnv() && (!cookies.apikey || cookies.apikey === '')) {
    dispatch(setError(error.apiKey));
    return;
  }

  let stopsNoLocation;
  if (isSolution) stopsNoLocation = createStopsNoLocation(fileObj.tours);

  const orderList = generateOrdersFromFileJSON(fileObj, stopsNoLocation);
  if (isEmpty(orderList) && !unassignedSol) {
    dispatch(setError(error.invalidFileFormat));
    dispatch(setSolutionJson({ jsonUserChange: false, jsonPaste: false, jsonPending: false }));
    return;
  }
  let loc;
  let returnLoc;
  const isImperial = user?.distance === 'imperial';

  if (!isSolution) {
    const fleet = fileObj.fleet;
    const groups = fileObj.plan?.groups;
    const configuration = fileObj?.configuration;
    const vehicleProfiles = fleet.profiles.map((profile) => {
      return {
        name: profile.name,
        avoid: profile.avoid,
        exclude: profile.exclude,
        fleetType: profile.type,
        departureTime: profile.departureTime,
        options: profile.options,
        traffic: profile.traffic,
        ignoreRouteViolations: profile.ignoreRouteViolations,
        violationMapping: profile.violationMapping,
        matrix: profile.matrix,
      };
    });
    const vehicles = fleet.types.map(({ shifts, ...item }) => {
      const limitsPresent =
        item.limits &&
        (item.limits.maxDistance !== undefined ||
          item.limits.shiftTime !== undefined ||
          item.limits.maxDrivingTime !== undefined ||
          (item.limits.stops &&
            (item.limits.stops.maxCount?.value !== undefined ||
              item.limits.stops.minCount?.value !== undefined)));

      if (limitsPresent) {
        dispatch(
          setUserParam({
            mapSettings: { ...user.mapSettings, addVehicleLimit: true },
          }),
        );
      }
      const vehicleObj = {
        ...item,
        costs: convertCostsToTourPlanner({ ...item.costs, isImperial }),
        limits: {
          ...item.limits,
          maxDistance: {
            enabled: item.limits?.maxDistance !== undefined,
            value: item.limits?.maxDistance,
          },
          shiftTime: {
            enabled: item.limits?.shiftTime !== undefined,
            value: item.limits?.shiftTime,
          },
          maxDrivingTime: {
            enabled: item.limits?.maxDrivingTime !== undefined,
            value: item.limits?.maxDrivingTime,
          },
          stops: {
            ...item.limits?.stops,
            maxCount: {
              enabled: item.limits?.stops?.maxCount?.value !== undefined,
              value: item.limits?.stops?.maxCount?.value,
            },
            minCount: {
              enabled: item.limits?.stops?.minCount?.value !== undefined,
              value: item.limits?.stops?.minCount?.value,
            },
          },
        },
      };

      if (typeof item.speedFactor !== 'undefined') {
        vehicleObj.speedFactor = item.speedFactor;
      }

      if (typeof item.traffic !== 'undefined' && item.traffic !== 'None') {
        fleet.traffic = item.traffic;
        vehicleObj.traffic = item.traffic;
      }

      return vehicleObj;
    });
    const start = fleet.types[0]?.shifts[0]?.start;
    const end = fleet.types[0]?.shifts[0]?.end;
    const startDate = start?.time;
    const startLoc = start?.location;
    const endLoc = end?.location;
    const recharges = fleet.types[0].shifts[0]?.recharges;
    const reloads = fleet.types[0].shifts[0]?.reloads;
    const restTimes = fleet.types[0].shifts[0]?.restTimes;
    const breaks = fleet.types[0].shifts[0]?.breaks;
    const types = fleet.types;
    const fuel = fleet.types[0]?.fuel;

    const defaultVal = { label: '', value: null };
    const location =
      startLoc && startLoc.lat && startLoc.lng
        ? { label: startTrans, value: { lat: startLoc.lat, lng: startLoc.lng } }
        : defaultVal;
    loc = location;
    const returnLocation =
      endLoc && endLoc.lat && endLoc.lng
        ? { label: endTrans, value: { lat: endLoc.lat, lng: endLoc.lng } }
        : defaultVal;
    returnLoc = returnLocation;

    dispatch(
      setTourParameter({
        vehicles,
        vehicleProfiles,
        traffic: fleet.traffic,
        offset: moment.parseZone(startDate).utcOffset(),
        index: idx,
        userChange: false,
        location,
        returnLocation,
        returnLocationMode: !isEqual(returnLocation, defaultVal) ? 'end' : 'start',
        uploadedLocations: true,
        recharges,
        reloads,
        restTimes,
        breaks,
        groups,
        configuration,
        start,
        end,
        types,
        fuel,
      }),
    );
  } else {
    dispatch(clearVehicleProfiles(index));
  }
  dispatch(setTourParameter({ fileType: 'json', index: idx }));
  dispatch(setOrders(orderList, isDemo, idx));

  let polygons = [];
  if (!jsonNoTerritories) {
    const { territories = [], groupAreas = [] } =
      !isSolution && generatePolygonsFromOrders(orderList);
    const avoidAreas = (!isSolution && generateAvoidAreasFromFileProblem(fileObj, orderList)) || [];
    const excludeAreas =
      (!isSolution && generateExcludeAreasFromFileProblem(fileObj, orderList)) || [];
    dispatch(
      setTerritoriesFromProblemFile({
        territories,
        avoidAreas,
        groupAreas,
        excludeAreas,
        index: idx,
      }),
    );
    polygons = [...territories, ...avoidAreas, ...groupAreas, ...excludeAreas];
  }

  const problemObj = isSolution ? generateRequestFromSolution(fileObj) : fileObj;
  let geo;

  if (!unassignedSol) {
    const start = fileObj.fleet?.types?.[0]?.shifts[0]?.start;

    geo = isSolution
      ? convertToGeoJSON(fileObj, stopsNoLocation, problemObj.plan.jobs, orderList, null, undefined)
          .geo
      : convertProblemToGeoJSON(
          problemObj,
          loc.value,
          returnLoc.value,
          orderList,
          startTrans,
          endTrans,
          null,
          polygons,
          start,
        );
    if (geo) {
      dispatch(setDisplayParam({ noMapMovement: false }));
      dispatch(setMapData({ geo }));
    } else dispatch(clearMapData());
  }

  const toSet = {
    territories: polygons,
    orders: orderList,
    json: problemObj,
    fileObj,
    uploaded: true,
    geo,
    filename,
    index: idx,
  };
  dispatch(setProblem(toSet));

  dispatch(recordUsageEvent({ event: USAGE_EVENTS.ORDERS_ADD, mode }));

  if (!isJsonViewer || isSolution) {
    planTour({
      dispatch,
      isSolution,
      cookies,
      user,
      oAuth,
      fileObj,
      stopsNoLocation,
      idx,
      keepOrders,
      isTDVRP,
    });
  }

  dispatch(setSolutionJson({ jsonUserChange: false, jsonPending: false, jsonPasting: false }));
};

export const fetchUrlFile = (url) => {
  return new Promise((resolve, reject) => {
    axios
      .get(url)
      .then((response) => {
        const fileContent = response.data;

        const urlObject = new URL(url);
        const filename = urlObject.pathname.split('/').pop();

        resolve({ fileContent, filename });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const handleTwoFiles = (files, user, cookies, oAuth, dispatch, error, index) => {
  const file1HasTours = files[0].fileContent.tours;
  const file2HasTours = files[1].fileContent.tours;

  if ((file1HasTours && file2HasTours) || (!file1HasTours && !file2HasTours)) {
    dispatch(setError(error.invalidFileContent));
    return;
  }

  const [problem, sol] = file1HasTours ? [files[1], files[0]] : [files[0], files[1]];
  const problemFile = problem.fileContent;
  const solutionFile = sol.fileContent;
  const typeIds = problemFile.fleet.types.map((type) => type.id);
  const solTypesUsed = solutionFile.tours.map((tour) => tour.typeId);
  const solAndProblemMatch = solTypesUsed.filter((value) => typeIds.includes(value));

  if (solAndProblemMatch.length === 0) {
    dispatch(setError(error.notMatchingProblemAndSol));
    return;
  }

  uploadProblemAndSolution(
    problemFile,
    solutionFile,
    'url',
    problem.filename,
    sol.filename,
    user,
    cookies,
    oAuth,
    dispatch,
    error,
    index,
    false,
  );
};

export const fetchAndProcessFiles = async (
  dispatch,
  oAuth,
  user,
  cookies,
  error,
  solution,
  setShowJsonPanel,
) => {
  const fileUrls = getFileUrlPath();
  if (!oAuth || !fileUrls || fileUrls.length === 0) return;
  if (fileUrls.length > 2) {
    dispatch(setError(error.fileMaximumTwo));
    return;
  }

  const fileHost = getFileHost();
  await Promise.all(fileUrls.map((url) => fetchUrlFile(fileHost ? `${fileHost}/${url}` : url)))
    .then((files) => {
      clearAll(dispatch, false, false, solution.show);
      if (files.length === 1) {
        const { fileContent, filename } = files[0];
        dispatch(setSolutionParam({ param: { name: filename } }));
        integrateFileText(
          JSON.stringify(fileContent, null, 2),
          'url',
          filename,
          user,
          cookies,
          oAuth,
          dispatch,
          true,
          error,
          solution.show,
          false,
        );
      } else if (files.length === 2) {
        handleTwoFiles(files, user, cookies, oAuth, dispatch, error, solution.show);
      }
      setShowJsonPanel(true);
    })
    .catch((err) => {
      if (err.response?.status === 404) dispatch(setError(error.fileNotFound));
      else dispatch(setError(error.unknownError));
    });
};

export const changeJsonOrderLocation = (jsonOrder, order) => {
  if (!jsonOrder || !order) return [];

  const pickup = order.Activity === 'pickup';
  set(
    jsonOrder,
    ['tasks', `${pickup ? 'pickups' : 'deliveries'}`, '0', 'places', '0', 'location'],
    { lat: order.Latitude, lng: order.Longitude },
  );
  return jsonOrder;
};

export const convertOrderToJson = (order) => {
  const pickup = order.Activity === 'pickup';
  const skills = order.Skills || [];
  const tasks = pickup
    ? {
        pickups: [
          {
            places: [
              {
                location: { lat: order.Latitude, lng: order.Longitude },
                duration: 1,
              },
            ],
            demand: order.Demand || [1],
          },
        ],
      }
    : {
        deliveries: [
          {
            places: [
              {
                location: { lat: order.Latitude, lng: order.Longitude },
                duration: 1,
              },
            ],
            demand: order.Demand || [1],
          },
        ],
      };
  return { id: order.InternalID, priority: order.Priority || 5, tasks, skills };
};

export const recalculateMapData = (
  dispatch,
  jsonObject,
  stopsNoLocation,
  territories,
  index,
  orders = undefined,
  startTrans,
  endTrans,
) => {
  const fleet = jsonObject?.fleet;
  const isSolution = !!jsonObject.tours;
  const problemObj = isSolution ? generateRequestFromSolution(jsonObject) : jsonObject;
  const orderList = orders || generateOrdersFromFileJSON(jsonObject, stopsNoLocation);
  const start = fleet?.types[0]?.shifts?.[0]?.start;
  const end = fleet?.types[0]?.shifts?.[0]?.end;
  const startLoc = start?.location;
  const endLoc = end?.location;

  const defaultVal = { label: '', value: null };
  const location =
    startLoc && startLoc.lat && startLoc.lng
      ? { label: startTrans, value: { lat: startLoc.lat, lng: startLoc.lng } }
      : defaultVal;
  const returnLocation =
    endLoc && endLoc.lat && endLoc.lng
      ? { label: endTrans, value: { lat: endLoc.lat, lng: endLoc.lng } }
      : defaultVal;

  const geo = isSolution
    ? convertToGeoJSON(
        jsonObject,
        stopsNoLocation,
        problemObj.plan.jobs,
        orderList,
        null,
        undefined,
      )?.geo
    : convertProblemToGeoJSON(
        problemObj,
        location.value,
        returnLocation.value,
        orderList,
        startTrans,
        endTrans,
        null,
        territories,
      );
  if (geo) {
    dispatch(setMapData({ geo }));
    if (index !== undefined) dispatch(setProblemParameter({ param: { geo }, index }));
  } else {
    dispatch(clearMapData());
  }
  dispatch(setSolutionJson({ jsonUserChange: false }));
};

export const recalculateSolutionParams = (
  jsonObject,
  dispatch,
  error,
  filename,
  stopsNoLocation,
  index,
  isDemo,
  ext,
  isImperial,
  orders,
  startTrans,
  endTrans,
  ordersUserChange,
  hasManualOrders,
  isDepotChange,
  user,
  jsonNoTerritories,
  tp,
) => {
  if (jsonNoTerritories && jsonObject?.fleet?.types) {
    jsonObject.fleet.types = jsonObject.fleet.types.map((vehicle) => {
      const { territories, ...rest } = vehicle;
      return rest;
    });
  }
  const isSolution = !!jsonObject.tours;
  const fileType = ext === 'json' ? ext : 'csv';
  let loc;
  let returnLoc;

  if (!isSolution) {
    const fleet = jsonObject.fleet;
    const vehicleProfiles = fleet.profiles.map((profile) => {
      return {
        name: profile.name,
        avoid: profile.avoid,
        exclude: profile.exclude,
        fleetType: profile.type,
        departureTime: profile.departureTime,
        options: profile.options,
        traffic: profile.traffic,
        ignoreRouteViolations: profile.ignoreRouteViolations,
        violationMapping: profile.violationMapping,
        matrix: profile.matrix,
      };
    });

    const vehicleLimit = user?.mapSettings?.addVehicleLimit;
    const vehicles = fleet.types.map(({ shifts, ...item }) => {
      let limits = {
        ...item.limits,
        maxDistance: {
          enabled: item.limits?.maxDistance !== undefined,
          value: item.limits?.maxDistance,
        },
        shiftTime: {
          enabled: item.limits?.shiftTime !== undefined,
          value: item.limits?.shiftTime,
        },
        maxDrivingTime: {
          enabled: item.limits?.maxDrivingTime !== undefined,
          value: item.limits?.maxDrivingTime,
        },
        stops: {
          ...item.limits?.stops,
          maxCount: {
            enabled: item.limits?.stops?.maxCount?.value !== undefined,
            value: item.limits?.stops?.maxCount?.value,
          },
          minCount: {
            enabled: item.limits?.stops?.minCount?.value !== undefined,
            value: item.limits?.stops?.minCount?.value,
          },
        },
      };

      if (!vehicleLimit) {
        limits = {
          ...limits,
          maxDistance: { ...limits.maxDistance, enabled: false },
          shiftTime: { ...limits.shiftTime, enabled: false },
          maxDrivingTime: { ...limits.maxDrivingTime, enabled: false },
          stops: {
            ...limits.stops,
            maxCount: { ...limits.stops?.maxCount, enabled: false },
            minCount: { ...limits.stops?.minCount, enabled: false },
          },
        };
      }

      const vehicleObj = {
        ...item,
        costs: convertCostsToTourPlanner({ ...item.costs, isImperial }),
        limits,
      };

      if (typeof item.speedFactor !== 'undefined') {
        vehicleObj.speedFactor = item.speedFactor;
      }
      if (typeof item.traffic !== 'undefined') {
        fleet.traffic = item.traffic;
        vehicleObj.traffic = item.traffic;
      } else {
        vehicleObj.traffic = 'automatic';
      }

      return vehicleObj;
    });

    const startDate = fleet.types[0].shifts[0].start.time;
    const start = fleet.types[0]?.shifts[0]?.start;
    const end = fleet.types[0]?.shifts[0]?.end;
    const startLoc = start?.location;
    const endLoc = end?.location;

    const defaultVal = { label: '', value: null };
    const location =
      startLoc && startLoc.lat && startLoc.lng
        ? {
            label: tp.location.label ?? startTrans,
            value: { lat: startLoc.lat, lng: startLoc.lng },
          }
        : defaultVal;
    loc = location;
    const returnLocation =
      endLoc && endLoc.lat && endLoc.lng
        ? {
            label: tp.returnLocation.label ?? endTrans,
            value: { lat: endLoc.lat, lng: endLoc.lng },
          }
        : defaultVal;
    returnLoc = returnLocation;

    dispatch(
      setTourParameter({
        vehicles,
        vehicleProfiles,
        traffic: fleet.traffic,
        offset: moment.parseZone(startDate).utcOffset(),
        jsonUserChange: true,
        index,
        start,
        end,
        location,
        returnLocation,
        returnLocationMode: tp?.returnLocationMode
          ? tp?.returnLocationMode
          : !isEqual(returnLocation, defaultVal)
          ? 'end'
          : 'start',
      }),
    );
  }
  dispatch(setTourParameter({ fileType, index }));

  const orderList =
    ordersUserChange && !isEmpty(orders)
      ? orders
      : generateOrdersFromFileJSON(jsonObject, stopsNoLocation, hasManualOrders && orders);

  if (!isEmpty(orderList)) {
    dispatch(setOrders(orderList, isDemo, index));
  }

  let polygons = [];
  if (!jsonNoTerritories) {
    const { territories = [], groupAreas = [] } =
      !isSolution && generatePolygonsFromOrders(orderList);
    const avoidAreas =
      (!isSolution && generateAvoidAreasFromFileProblem(jsonObject, orderList)) || [];
    const excludeAreas =
      (!isSolution && generateExcludeAreasFromFileProblem(jsonObject, orderList)) || [];
    dispatch(
      setTerritoriesFromProblemFile({ territories, avoidAreas, groupAreas, excludeAreas, index }),
    );
    polygons = [...territories, ...avoidAreas, ...groupAreas, ...excludeAreas];
  }

  const problemObj = isSolution ? generateRequestFromSolution(jsonObject) : jsonObject;
  const geo = isSolution
    ? convertToGeoJSON(
        jsonObject,
        stopsNoLocation,
        problemObj.plan.jobs,
        orderList,
        null,
        undefined,
      )?.geo
    : convertProblemToGeoJSON(
        problemObj,
        loc.value,
        returnLoc.value,
        orderList,
        startTrans,
        endTrans,
        null,
        polygons,
      );

  const toSet = {
    territories: polygons,
    orders: orderList,
    json: problemObj,
    fileObj: jsonObject,
    uploaded: true,
    geo,
    filename,
    index,
  };

  dispatch(setProblem(toSet));
  if (!isDepotChange)
    recalculateMapData(
      dispatch,
      jsonObject,
      stopsNoLocation,
      polygons,
      index,
      orderList,
      startTrans,
      endTrans,
    );

  dispatch(setSolutionJson({ jsonUserChange: false }));
};

export const readFiles = (files) => {
  return Promise.all(
    files.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve({ fileContent: JSON.parse(reader.result), filename: file.name });
        reader.onerror = reject;
        reader.readAsText(file);
      });
    }),
  );
};

export const handleJsonDrop = (
  dispatch,
  files,
  mode,
  error,
  appMode,
  cookies,
  oAuth,
  user,
  browseEl,
  index,
  keepOrders,
  startTrans,
  endsTrans,
) => {
  if (files.length > 2) {
    dispatch(setError(error.fileMaximumTwo));
    return;
  }
  if (files.length === 2) {
    readFiles([files[0], files[1]])
      .then((jsonFiles) => {
        handleTwoFiles(jsonFiles, user, cookies, oAuth, dispatch, error, index);
      })
      .catch(() => {
        dispatch(setError(error.unableToReadFile));
      });
    return;
  }
  if (appMode.includes(APP_MODES.OCR)) {
    if (!/\.(csv|json|jpg|jpeg|png|gif)$/.test(files[0].name)) {
      dispatch(setError(error.invalidFileExtension));
      return;
    }
  } else if (appMode.includes(APP_MODES.OCR_AWS, APP_MODES.OCR_AWS_EDIT)) {
    if (!/\.(csv|json|jpg|jpeg|png|gif|pdf)$/.test(files[0].name)) {
      dispatch(setError(error.invalidFileExtension));
      return;
    }
  } else if (!/\.(csv|json)$/.test(files[0].name)) {
    dispatch(setError(error.invalidFileExtension));
    return;
  }

  const reader = new FileReader();
  reader.readAsText(files[0]);

  reader.addEventListener('loadend', () => {
    const { result } = reader;
    const filename = files[0].name;

    if (filename.endsWith('.json')) {
      try {
        const resJson = JSON.parse(result);
        if (resJson?.tours?.length === 0 && (resJson.notices || resJson.unassigned)) {
          dispatch(
            setSolutionParam({
              param: { notices: resJson.notices || [], unassigned: resJson.unassigned },
            }),
          );
        }

        integrateFileText(
          result,
          mode,
          filename,
          user,
          cookies,
          oAuth,
          dispatch,
          true,
          error,
          index,
          keepOrders,
          false,
          false,
          startTrans,
          endsTrans,
        );
      } catch (err) {
        dispatch(setError(err));
      }

      browseEl.current.value = '';
    }
  });
};
